import { Close } from '@mui/icons-material';
import { Box, Button, IconButton, Modal, Stack } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import AuthInput from '../../../components/AuthController/AuthInput';
import AuthTelInput from '../../../components/AuthController/AuthTelInput';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMessage } from '../../../../base/MessageProvider';
import { matchIsValidTel } from 'mui-tel-input';
import { otpSignup } from '../../../actions/auth';
import { replaceCountryCode } from '../../../../utils';
import { Bars } from 'react-loader-spinner';
import clsx from 'clsx';

const boxStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 400,
    width: '90%',
    bgcolor: 'white',
    paddingX: 5,
    paddingY: 3,
    borderRadius: '18px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center' 
}

const validationSchema = Yup.object().shape({
    firstName: Yup.string().required('First name is required')
        .test("Valid firstName", "Invalid first name", (value) => /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/g.test(value)),
    lastName: Yup.string().required('Last name is required')
        .test("Valid lastName", "Invalid last name", (value) => /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/g.test(value)),
    mobileNo: Yup.string().required('Mobile number is required')
        .test("phone", 'Invalid mobile number', (value) => matchIsValidTel(value)),
});

const SignupModal = ({ open, onClose }) => {
    const [errorMessage, setErrorMessage] = useState("");
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const alert = useMessage();

    const loading = useSelector((state) => state.artistAuthReducer?.loading);

    const {
        control, clearErrors, setError,
        formState: { errors }, reset, handleSubmit
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            firstName: '',
            mobileNo: '',
            lastName: '',
        },
    });

    useEffect(() => {
        clearErrors();
        reset();
    }, []);

    const onClickLogin = () => {
        navigate('/artist/auth/login');
    }

    const onSubmit = async (data) => {
        clearErrors();
        const request = {
            firstName: data.firstName,
            lastName: data.lastName,
            mobileNo: replaceCountryCode(data.mobileNo),
        };
        dispatch(otpSignup(request, (signupOtp) => {
            navigate('/artist/auth/signup-otp', { replace: false });
            alert.showInfo(signupOtp, { timeout: 5000 });
        }, (error) => {
            setError('mobileNo', {
                type: 'manual',
                message: error?.message ?? "Something went wrong!!"
            });
        }))
    }

    return (
        <Modal disableAutoFocus sx={{ bgcolor: '#F4F4F499'}} hideBackdrop open={open}>
            <Box className="shadow-[0_0_14px_grey]" sx={boxStyle}>
                <IconButton onClick={onClose} className='absolute top-3 right-3' size='small'> 
                    <Close className='text-xl' />
                </IconButton>
                <Box className="mb-6">
                    <h2 className="font-['Poppins-Medium'] font-[500] text-[16px]">SIGN UP</h2>
                </Box>
                <Stack direction={'row'} spacing={2} justifyContent="space-between" my={1}>
                    <AuthInput 
                        placeholder='First Name' hookFormControl={control} varName="firstName" size='small'
                        errorMessage={errors?.firstName?.message} 
                    />
                    <AuthInput 
                        placeholder='Last Name' hookFormControl={control} varName="lastName" size='small' 
                        errorMessage={errors?.lastName?.message} 
                    />
                </Stack>
                <div className="w-full flex flex-col gap-3 my-2">
                    <AuthTelInput 
                        size='small' placeholder='Phone Number' hookFormControl={control} varName="mobileNo"
                        errorMessage={errors?.mobileNo?.message} 
                    />
                </div>
                <Button onClick={handleSubmit(onSubmit)} disabled={loading} className={clsx("bg-[#2F80ED] flex gap-3 justify-center text-[#FFF] w-full rounded-full my-2 font-['Poppins-Medium'] font-[500]", { "bg-opacity-70": loading })}>
                    Sign Up
                    <Bars
                        height="20"
                        width={'20'}
                        color="#FFFFFF"
                        ariaLabel="bars-loading"
                        visible={loading}
                    />
                </Button>
                <p className="font-['Matter-Regular'] font-[400] text-[12px] text-center text-[#1F1E1E]">
                    Already Have An Account? <a onClick={onClickLogin} className="text-[#2F80ED] hover:underline cursor-pointer">Log In</a>
                </p>
            </Box>
        </Modal>
    );
}

export default React.memo(SignupModal, (prevProps, nextProps) => {
    return prevProps.open === nextProps.open
});